/**
 * Deep merge utility for content overrides
 *
 * Recursively merges two objects, with override values taking precedence over default values.
 * Arrays are replaced entirely (not merged).
 *
 * Special values:
 * - `undefined`: Skip this property (don't override)
 * - `null`: Delete this property from the result
 * - Other values: Merge or override as normal
 *
 * @param defaultContent - Base content object
 * @param override - Override content object
 * @returns Merged content object
 *
 * @example
 * ```typescript
 * // Override a property
 * const defaultNav = { header: { cta: { text: "Default" }, mainNav: [...] } };
 * const override = { header: { cta: { text: "Override" } } };
 * const merged = deepMerge(defaultNav, override);
 * // Result: { header: { cta: { text: "Override" }, mainNav: [...] } }
 *
 * // Delete a property
 * const defaultContact = { contact: { cta: { text: "Contact", href: "#" }, title: "Title" } };
 * const override = { contact: { cta: null } };
 * const merged = deepMerge(defaultContact, override);
 * // Result: { contact: { title: "Title" } } - cta is removed
 * ```
 */
export const deepMerge = <T>(defaultContent: T, override: Partial<T> | undefined | null): T => {
    // If no override, return default
    if (!override) {
        return defaultContent;
    }

    // If default is undefined or null, return override
    if (!defaultContent) {
        return override as T;
    }

    // If either value is not an object, return override
    if (typeof defaultContent !== "object" || typeof override !== "object") {
        return override as T;
    }

    // If either is an array, replace entirely (don't merge arrays)
    if (Array.isArray(defaultContent) || Array.isArray(override)) {
        return override as T;
    }

    // Deep merge objects
    const result = { ...defaultContent } as T;

    for (const key in override) {
        if (Object.prototype.hasOwnProperty.call(override, key)) {
            const overrideValue = override[key];
            const defaultValue = (defaultContent as Record<string, unknown>)[key];

            if (overrideValue === null) {
                // null is used as a deletion marker - remove the property
                delete (result as Record<string, unknown>)[key];
            } else if (overrideValue !== undefined) {
                // Merge or override the property
                (result as Record<string, unknown>)[key] = deepMerge(defaultValue, overrideValue);
            }
        }
    }

    return result;
};

/**
 * Merge content with type safety
 *
 * Generic helper for merging any content objects with proper TypeScript types.
 * This is a convenient alias for deepMerge with clearer naming for content merging.
 *
 * Supports both overriding and deleting properties:
 * - Set a property to override it
 * - Set a property to `null` to delete it from the default content
 * - Omit a property (or set to `undefined`) to keep the default value
 *
 * @param defaultContent - Default content from component/section MDX files
 * @param overrideContent - Override content from page MDX files
 * @returns Merged content object
 *
 * @example
 * ```typescript
 * // Merge navigation
 * const nav = overrideContent(defaultNav, pageNav);
 *
 * // Merge any content
 * const hero = overrideContent(defaultHero, pageHero);
 *
 * // Remove a property (e.g., remove CTA from contact section)
 * const contact = overrideContent(defaultContact, { cta: null });
 * // Result: contact.cta is undefined (removed)
 * ```
 */
export const overrideContent = <T>(defaultContent: T, overrideContent: Partial<T> | undefined | null): T => {
    return deepMerge(defaultContent, overrideContent);
};
